import { enhancedApi } from '@store/generated/payments';

/**
 * Feature-specific tag types for cache management
 */
const PAYMENTS_TAG_TYPES = [
  'StripeConnectStatus',
  'StripeAccount',
  'StripeInvoices',
  'PatientPaymentMethods',
  'PatientInvoices',
] as const;

/**
 * Payments API - extends the generated OpenAPI client
 * Provides Stripe Connect integration endpoints
 */
export const paymentsApi = enhancedApi.enhanceEndpoints({
  addTagTypes: PAYMENTS_TAG_TYPES,
  endpoints: {
    // Enhance generated endpoints with cache tags
    postV2ProviderStripeConnect: {
      invalidatesTags: [
        { type: 'StripeConnectStatus' as const },
        { type: 'StripeAccount' as const },
      ],
    },

    getV2ProviderStripeConnectStatus: {
      providesTags: [{ type: 'StripeConnectStatus' as const }],
    },

    postV2ProviderStripeDashboardLink: {
      // No cache invalidation needed for dashboard link generation
    },

    getV2ProviderStripeInvoices: {
      providesTags: result => [
        { type: 'StripeInvoices' as const },
        ...(result?.invoices || []).map(invoice => ({
          type: 'StripeInvoices' as const,
          id: invoice.id,
        })),
      ],
    },

    postV2ProviderPaymentRequests: {
      // Payment requests don't need specific cache management at this level
      // They should invalidate patient/appointment data as needed in the UI
    },

    // Patient Payment Methods endpoints
    getV2ProviderPatientsByPatientIdPaymentMethods: {
      providesTags: (result, _error, { patientId }) =>
        result?.payment_methods
          ? [
              ...result.payment_methods.map(pm => ({
                type: 'PatientPaymentMethods' as const,
                id: pm.id,
              })),
              { type: 'PatientPaymentMethods' as const, id: patientId },
            ]
          : [{ type: 'PatientPaymentMethods' as const, id: patientId }],
    },

    getV2ProviderPatientsByPatientIdPaymentMethodsStatus: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'PatientPaymentMethods' as const, id: `${patientId}-status` },
      ],
    },

    postV2ProviderPatientsByPatientIdPaymentMethodsSetup: {
      // Setup intent creation doesn't need cache invalidation
    },

    postV2ProviderPatientsByPatientIdPaymentMethodsConfirm: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientPaymentMethods' as const, id: patientId },
        { type: 'PatientPaymentMethods' as const, id: `${patientId}-status` },
      ],
    },

    deleteV2ProviderPatientsByPatientIdPaymentMethodsAndPaymentMethodId: {
      invalidatesTags: (_result, _error, { patientId, paymentMethodId }) => [
        { type: 'PatientPaymentMethods' as const, id: paymentMethodId },
        { type: 'PatientPaymentMethods' as const, id: patientId },
        { type: 'PatientPaymentMethods' as const, id: `${patientId}-status` },
      ],
    },

    postV2ProviderPatientsByPatientIdPaymentMethodsAndPaymentMethodIdDefault: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientPaymentMethods' as const, id: patientId },
      ],
    },

    // Invoice URLs - fetch fresh on each click, never cache
    getV2ProviderPatientsByPatientIdInvoicesAndInvoiceIdUrls: {
      // No cache tags — URLs expire quickly, always fetch fresh
      keepUnusedDataFor: 0,
    },

    // Patient Invoices endpoint
    getV2ProviderPatientsByPatientIdInvoices: {
      providesTags: (result, _error, { patientId }) =>
        result?.invoices
          ? [
              ...result.invoices.map(invoice => ({
                type: 'PatientInvoices' as const,
                id: invoice.id,
              })),
              { type: 'PatientInvoices' as const, id: patientId },
            ]
          : [{ type: 'PatientInvoices' as const, id: patientId }],
    },
  },
});

// Export hooks with user-friendly names
export const {
  // Stripe Connect hooks
  usePostV2ProviderStripeConnectMutation: useCreateStripeConnectMutation,
  useGetV2ProviderStripeConnectStatusQuery: useGetStripeConnectStatusQuery,
  usePostV2ProviderStripeDashboardLinkMutation: useCreateStripeDashboardLinkMutation,
  useGetV2ProviderStripeInvoicesQuery: useGetStripeInvoicesQuery,
  useLazyGetV2ProviderStripeInvoicesQuery: useLazyGetStripeInvoicesQuery,
  usePostV2ProviderPaymentRequestsMutation: useSendPaymentRequestMutation,

  // Patient Payment Methods hooks
  useGetV2ProviderPatientsByPatientIdPaymentMethodsQuery: useGetPatientPaymentMethodsQuery,
  useGetV2ProviderPatientsByPatientIdPaymentMethodsStatusQuery: useGetPatientPaymentMethodStatusQuery,
  usePostV2ProviderPatientsByPatientIdPaymentMethodsSetupMutation: useSetupPatientPaymentMethodMutation,
  usePostV2ProviderPatientsByPatientIdPaymentMethodsConfirmMutation: useConfirmPatientPaymentMethodMutation,
  useDeleteV2ProviderPatientsByPatientIdPaymentMethodsAndPaymentMethodIdMutation: useDeletePatientPaymentMethodMutation,
  usePostV2ProviderPatientsByPatientIdPaymentMethodsAndPaymentMethodIdDefaultMutation: useSetDefaultPatientPaymentMethodMutation,

  // Patient Invoices hooks
  useGetV2ProviderPatientsByPatientIdInvoicesQuery: useGetPatientInvoicesQuery,

  // Invoice URLs - lazy query for on-click fresh URL fetching
  useLazyGetV2ProviderPatientsByPatientIdInvoicesAndInvoiceIdUrlsQuery: useLazyGetInvoiceUrlsQuery,
} = paymentsApi;
